import { Link, useNavigate } from "react-router-dom";
import { LogOut, Menu, User, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { LOGO_URL } from "../lib/site-data";
import { useAuth } from "../lib/auth";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const { isAuthenticated, user, logout } = useAuth();
  const menuRef = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const firstName = (user?.full_name || "").split(" ")[0] || "Account";

  const goToSection = (id) => {
    setOpen(false);
    if (window.location.pathname !== "/") {
      navigate("/", { replace: false });
      setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
      }, 80);
    } else {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handleLogout = async () => {
    await logout();
    setMenuOpen(false);
    setOpen(false);
    navigate("/");
  };

  return (
    <header
      data-testid="site-navbar"
      className="sticky top-0 z-40 bg-background/85 backdrop-blur-md border-b border-border"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
        <Link
          to="/"
          data-testid="navbar-logo-link"
          className="flex items-center gap-3"
        >
          <img
            src={LOGO_URL}
            alt="CV Folks"
            width="160"
            height="48"
            fetchpriority="high"
            className="h-12 w-auto object-contain"
          />
        </Link>

        <nav className="hidden md:flex items-center gap-6 text-sm font-medium">
          <Link
            to="/ai-cv-maker-builder"
            data-testid="nav-ai-cv-maker"
            className="hover:text-brand transition-colors"
          >
            AI CV Builder
          </Link>
          <Link
            to="/services/cv-writing"
            data-testid="nav-services-cv-writing"
            className="hover:text-brand transition-colors"
          >
            Hire CV Writer
          </Link>
          <Link
            to="/pricing"
            data-testid="nav-pricing"
            className="hover:text-brand transition-colors"
          >
            Pricing
          </Link>
          <Link
            to="/career-resources"
            data-testid="nav-resources"
            className="hover:text-brand transition-colors"
          >
            Resources
          </Link>
          <Link
            to="/about"
            data-testid="nav-about"
            className="hover:text-brand transition-colors"
          >
            About
          </Link>
          <Link
            to="/contact"
            data-testid="nav-contact"
            className="hover:text-brand transition-colors"
          >
            Contact
          </Link>
        </nav>

        <div className="hidden md:flex items-center gap-3">
          {isAuthenticated ? (
            <div className="relative" ref={menuRef}>
              <button
                type="button"
                data-testid="navbar-user-menu"
                onClick={() => setMenuOpen((v) => !v)}
                className="inline-flex items-center gap-2 rounded-full border border-border bg-background px-4 py-2 text-sm font-semibold hover:border-brand hover:text-brand transition"
              >
                <User className="h-4 w-4" />
                {firstName}
              </button>
              {menuOpen && (
                <div
                  data-testid="navbar-user-dropdown"
                  className="absolute right-0 mt-2 w-52 rounded-2xl border border-border bg-card shadow-xl p-2 z-50"
                >
                  <Link
                    to="/dashboard"
                    onClick={() => setMenuOpen(false)}
                    data-testid="navbar-dashboard-link"
                    className="block rounded-xl px-3 py-2 text-sm font-medium hover:bg-muted"
                  >
                    Dashboard
                  </Link>
                  <Link
                    to="/my-cvs"
                    onClick={() => setMenuOpen(false)}
                    data-testid="navbar-my-cvs-link"
                    className="block rounded-xl px-3 py-2 text-sm font-medium hover:bg-muted"
                  >
                    My CVs
                  </Link>
                  <button
                    type="button"
                    data-testid="navbar-logout"
                    onClick={handleLogout}
                    className="w-full text-left inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-medium text-destructive hover:bg-muted"
                  >
                    <LogOut className="h-4 w-4" /> Sign out
                  </button>
                </div>
              )}
            </div>
          ) : (
            <>
              <Link
                to="/login"
                data-testid="navbar-login"
                className="text-sm font-semibold text-foreground/80 hover:text-brand transition"
              >
                Log in
              </Link>
              <Link
                to="/register"
                data-testid="navbar-register"
                className="inline-flex items-center justify-center rounded-full border border-border bg-background px-4 py-2 text-sm font-semibold hover:border-brand hover:text-brand transition"
              >
                Sign up
              </Link>
            </>
          )}
          <button
            data-testid="navbar-buy-now"
            onClick={() => goToSection("two-ways")}
            className="btn-primary inline-flex items-center justify-center rounded-full px-5 py-2.5 text-sm font-semibold"
          >
            Buy Now
          </button>
        </div>

        <button
          aria-label="Menu"
          data-testid="navbar-mobile-toggle"
          className="md:hidden p-2"
          onClick={() => setOpen(!open)}
        >
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {open && (
        <div
          data-testid="navbar-mobile-menu"
          className="md:hidden bg-background border-t border-border"
        >
          <div className="px-6 py-4 flex flex-col gap-3 text-base">
            {[
              ["/ai-cv-maker-builder", "AI CV Builder"],
              ["/services/cv-writing", "Hire CV Writer"],
              ["/pricing", "Pricing"],
              ["/career-resources", "Resources"],
              ["/about", "About"],
              ["/contact", "Contact"],
            ].map(([path, label]) => (
              <Link
                key={path}
                to={path}
                onClick={() => setOpen(false)}
                className="text-left py-2"
                data-testid={`nav-mobile-${path.replace(/\//g, "-").slice(1)}`}
              >
                {label}
              </Link>
            ))}
            <div className="border-t border-border my-2" />
            {isAuthenticated ? (
              <>
                <Link
                  to="/dashboard"
                  onClick={() => setOpen(false)}
                  data-testid="nav-mobile-dashboard"
                  className="py-2 font-medium"
                >
                  Dashboard
                </Link>
                <button
                  onClick={handleLogout}
                  data-testid="nav-mobile-logout"
                  className="text-left py-2 font-medium text-destructive"
                >
                  Sign out
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  onClick={() => setOpen(false)}
                  data-testid="nav-mobile-login"
                  className="py-2 font-medium"
                >
                  Log in
                </Link>
                <Link
                  to="/register"
                  onClick={() => setOpen(false)}
                  data-testid="nav-mobile-register"
                  className="py-2 font-medium"
                >
                  Sign up
                </Link>
              </>
            )}
            <button
              data-testid="navbar-mobile-buy"
              onClick={() => goToSection("two-ways")}
              className="btn-primary mt-2 rounded-full px-5 py-3 text-sm font-semibold"
            >
              Buy Now
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
